import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import {
  customObjectsService,
  CustomObjectType,
  CustomObjectRecord,
} from '@/services/custom-objects.service';
import { Database, Plus, ExternalLink, ChevronDown } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  contactUid: string;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * Renders a "Related records" panel on the Contact detail page. Shows custom
 * object records linked to this contact, grouped by type. The "+ New" picker
 * surfaces only types that link to a Contact.
 */
export const ContactCustomRecords: React.FC<Props> = ({ contactUid }) => {
  const nav = useNavigate();
  const [types, setTypes] = useState<CustomObjectType[]>([]);
  const [records, setRecords] = useState<CustomObjectRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const refresh = async () => {
    const [t, r] = await Promise.all([
      customObjectsService.listTypes(false),
      customObjectsService.listRecords({ contactUid, limit: 100 }),
    ]);
    setTypes(t);
    setRecords(r.items);
  };

  useEffect(() => {
    setLoading(true);
    refresh().catch(() => undefined).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactUid]);

  // Filter to types this contact can have. We hide catalogue-only types
  // (Doctors, Menu, Listings) from the "+ New" picker since they shouldn't
  // be created per-contact.
  const contactLinkedTypes = useMemo(
    () => types.filter((t) => t.linkToContact),
    [types],
  );

  // Group records by typeKey, preserving newest-first within group.
  const groups = useMemo(() => {
    const byType = new Map<string, CustomObjectRecord[]>();
    for (const r of records) {
      const arr = byType.get(r.typeKey) ?? [];
      arr.push(r);
      byType.set(r.typeKey, arr);
    }
    return Array.from(byType.entries()).map(([key, items]) => {
      const type = types.find((t) => t.key === key);
      return { key, type, items };
    });
  }, [records, types]);

  if (loading) {
    return (
      <Card>
        <SectionHeader title="Related records" icon={<Database size={16} />} className="mb-4" />
        <div className="space-y-2">
          {[0, 1].map((i) => <div key={i} className="skeleton h-16 rounded-lg" />)}
        </div>
      </Card>
    );
  }

  // No types installed at all → hide the section to avoid empty noise.
  if (contactLinkedTypes.length === 0) return null;

  return (
    <Card>
      <SectionHeader
        title="Related records"
        icon={<Database size={16} />}
        subtitle={records.length === 0 ? 'No records yet for this contact.' : `${records.length} record${records.length === 1 ? '' : 's'}`}
        action={
          <div className="relative">
            <Button
              size="sm"
              variant="secondary"
              iconLeft={<Plus size={12} />}
              onClick={() => setPickerOpen((v) => !v)}
            >
              New <ChevronDown size={11} className="ml-0.5" />
            </Button>
            {pickerOpen && (
              <div className="absolute right-0 top-full z-20 mt-1 w-56 rounded-xl border border-border bg-surface shadow-soft-lg overflow-hidden">
                {contactLinkedTypes.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => {
                      setPickerOpen(false);
                      // Create a new record pre-linked to this contact.
                      // Pass contactUid via query for the editor to pick up.
                      nav(`/custom-objects/${t.key}/new?contactUid=${encodeURIComponent(contactUid)}`);
                    }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-surface-alt"
                  >
                    <div className="font-medium text-text-primary">{t.label}</div>
                    {t.description && (
                      <div className="text-2xs text-text-muted truncate">{t.description}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        }
        className="mb-4"
      />

      {groups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-text-muted">
          No records linked to this contact yet. Click "New" above to create one.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.key}>
              <div className="mb-2 flex items-center justify-between">
                <div className="font-display text-sm font-semibold text-text-primary">
                  {g.type?.pluralLabel ?? g.type?.label ?? g.key}
                  <span className="ml-2 rounded-full bg-surface-alt px-1.5 py-0.5 text-2xs font-normal text-text-muted">
                    {g.items.length}
                  </span>
                </div>
                <Link
                  to={`/custom-objects/${g.key}?contactUid=${encodeURIComponent(contactUid)}`}
                  className="inline-flex items-center gap-1 text-2xs text-text-muted hover:text-primary"
                >
                  See all <ExternalLink size={10} />
                </Link>
              </div>
              <div className="space-y-1.5">
                {g.items.slice(0, 5).map((r) => {
                  const previewFields = (g.type?.fields ?? []).slice(0, 3);
                  return (
                    <Link
                      key={r.recordUid}
                      to={`/custom-objects/${g.key}/${r.recordUid}`}
                      className={clsx(
                        'flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2 transition-colors',
                        'hover:border-primary/40 hover:bg-surface-alt',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-text-primary">
                          {r.title ?? <span className="italic text-text-muted">untitled</span>}
                        </div>
                        {previewFields.length > 0 && (
                          <div className="mt-0.5 truncate text-xs text-text-muted">
                            {previewFields
                              .map((f) => `${f.label}: ${formatValue(r.values?.[f.key])}`)
                              .join(' · ')}
                          </div>
                        )}
                      </div>
                      <div className="text-2xs text-text-muted whitespace-nowrap">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </div>
                    </Link>
                  );
                })}
                {g.items.length > 5 && (
                  <Link
                    to={`/custom-objects/${g.key}?contactUid=${encodeURIComponent(contactUid)}`}
                    className="block rounded-lg px-3 py-2 text-center text-2xs text-text-muted hover:text-primary hover:bg-surface-alt"
                  >
                    + {g.items.length - 5} more
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
